import { useQuery } from "@tanstack/react-query";
import { CountryCode, TrainIdentifier } from "@vygruppen/vy-train-map";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import { formatISO } from "date-fns";
import { TrainEventTypeEnum } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainEventTypeEnum";
import { OriginSystem } from "features/History/HistoryDetail/fetchHistoryDetail";
import { useMemo } from "react";
import { VisibilityResponse } from "shared/form/visibilitySchema";
import { Distribution } from "shared/types/historyDetail";
import { Nullish } from "shared/utils/objectUtils";

type TrainInformationResponse = {
  uuid: string;
  countryCode: CountryCode;
  version: number;
  createdAt: string;
  updatedAt: string;
  originSystem: OriginSystem;
  type: { type: TrainEventTypeEnum; group: string; label?: string };
  state: "OPEN" | "CLOSED" | "REPLACED" | "DEPUBLISHED";
  isVisible: boolean;
  visibility: VisibilityResponse;
  distributions: Distribution[];
  trainIdentifier: TrainIdentifier;
  lineNumber?: string;
};

export type TrainInformation = Omit<TrainInformationResponse, "type"> & {
  type: TrainEventTypeEnum;
};

function normaliseData(
  rawData: Nullish<Array<TrainInformationResponse>>,
): Nullish<Array<TrainInformation>> {
  return rawData?.map((tir) => ({
    ...tir,
    type: tir.type.type,
  }));
}

export function useAssistanceInTimePeriod(
  countryCode: CountryCode | undefined,
  fromTime: Date | string,
  toTime: Date | string,
) {
  const params = useMemo(
    () => ({
      fromTime: formatISO(fromTime),
      toTime: formatISO(toTime),
    }),
    [fromTime, toTime],
  );

  const { data, status } = useQuery({
    queryKey: [
      "alternativeTransport",
      "assistance",
      countryCode,
      fromTime,
      toTime,
    ],
    enabled: Boolean(countryCode) && Boolean(fromTime) && Boolean(toTime),
    queryFn: async ({ signal }) => {
      const response = await axiosClient.get<Array<TrainInformationResponse>>(
        `${getBackendUrl()}/alternative-transport-planning/assistance/${countryCode}`,
        {
          signal,
          params,
        },
      );
      return normaliseData(response.data);
    },
    refetchInterval: 60 * 1000, // Every minute
    retry: false,
  });

  return { data, status };
}
